import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";

import { RideGrid } from "@rimac-seguros/ride-system-components";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import useResponsiveDetection from "@/hooks/useResponsiveDetection";

import { useLoadInitialData } from "../../hooks/useLoadInitialData";
import { useQuote } from "../../hooks/useQuote";
import { CreditDebtAmount } from "./CreditDebtAmount/index";
import { CreditTermSetting } from "./CreditTermSetting/index";
import { RepaymentPercentage } from "./RepaymentPercentage/index";
import { PaymentFrequency } from "./PaymentFrequency/index";
import { DownloadQuote } from "./DownloadQuote/index";
import { CardDetailPlan } from "../../../shared/components/CardDetailPlan/index";
import { CreditBankSelector } from "./CreditBankSelector";
import { planSelected, setCurrencyPlan } from "@/redux/actions/plans";
import { StateMachine } from "@/utils/consts";
import { usePlansContext } from "../../context/PlansContext";
import { ModalError } from "@/components/molecules/ModalError";
import { setViewModalError } from "@/redux/actions/global";
import { compareSumAssured, focusFirstErrorField } from "@/utils/Utils";
import { getProductOptions } from "@/utils/product";
import helpers from "@/utils/helpers";

import "./styles.scss";
import { setSuscriptionAnswers } from '@/redux/actions/vida'
import { useQuoteService } from '@/services/quote.service'

const SectionPlans = forwardRef(({ currencyOptions, urlHome, analyticsDetailAction }, ref) => {
  const initiatingData = useRef(true);
  const { isSmallScreen } = useResponsiveDetection();
  const detailPlanSelected = useSelector((state) => state.plans.planSelected);
  const { state } = useSelector((state) => state.global);
  const userData = useSelector((state) => state.vida.userData);
  const [isGreatterValue, setIsGreaterValue] = useState(false);
  const [showModalError, setShowModalError] = useState(false);
  const currencyPlan = useSelector((state) => state.plans.currencyPlan);
  const { maxAmountQuote } = useSelector(
      (state) => state.plans
    );

  const {
    register,
    watch,
    setValue,
    getValues,
    trigger,
    clearErrors,
    getFieldState,
    formState: { errors },
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      creditBank: "",
      currencyType: currencyPlan,
    },
  });

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const isValid = await trigger();
      if (!isValid) focusFirstErrorField({ getFieldState });
      return isValid
    },
    getValues,
  }));

  const {
    arraySumAssured,
    arraySavingTime,
    arrayReturnPercentage,
    sumAssured,
    savingTime,
    arrayFinancialEntities,
    arrayPaymentFrequency,
    initialReturnPercentage,
    returnPercentage,
    financialEntity,
    frequency,
    firstLoad,
    setSumAssured,
    setSavingTime,
    setReturnPercentage,
    setFrequency,
    resetList,
    getReturnPercentage,
    setCurrency,
  } = useLoadInitialData({ initiatingData, setShowModalError });
  const {
    loadingSavingTime,
    loadingReturnPercentage,
    isLoadingData,
    loadingSumAssured,
    setLoadingSumAssured,
    setLoadingQuote,
  } = usePlansContext();

  const { sendQuote } = useQuote({ setShowModalError });
  const { getSuscriptionValidation } = useQuoteService();
  const dispatch = useDispatch();

  const { productName } = getProductOptions();
  const isCreditMaxAmount = Number(sumAssured?.value) > maxAmountQuote;

  useEffect(() => {
    if (userData.indPrevPageApp) {
      helpers.sendMessageToApp("EMBEDDED-APP-SHOW-TOPBAR");
    }
  }, []);

  useEffect(() => {
    if (state === StateMachine[4]) {
      if (userData.indPrevPageApp) {
        helpers.sendMessageToApp("error-api");
      }
      return dispatch(setViewModalError(true));
    }
  }, [state]);

  const handleSelectSumAssured = async (item) => {
    if (firstLoad) return;
    setLoadingQuote(true);
    setSumAssured(item);
    updateStore("sumAssured", item);
    setIsGreaterValue(compareSumAssured(item, sumAssured));

    if (item && !initiatingData.current) {
      await getSuscriptionValidation({
        descAtributo: currencyPlan,
        valorAtributo: item.valor
      });
    }
    await resetList(item);
  };

  const handleSelectSavingTime = async (item) => {
    setSavingTime(item);
    updateStore("savingTime", item);
    setIsGreaterValue(compareSumAssured(item, savingTime));
    if (!firstLoad && !initiatingData.current) {
      await getReturnPercentage(item);
    }
  };

  const handleSelectReturnPercentage = async (item) => {
    setReturnPercentage(item);
    updateStore("returnPercentage", item);

    const shouldSendQuote = !firstLoad && !initiatingData.current && !isCreditMaxAmount;
    const shouldSkipDueToMaxAmount = !firstLoad && !initiatingData.current && isCreditMaxAmount;

    if (shouldSkipDueToMaxAmount) setLoadingQuote(false);

    if (shouldSendQuote) {
      await sendQuote({
        sumAssured,
        savingTime,
        returnPercentage: item,
        frequency,
      });
    }

    initiatingData.current = false;
  };

  const handleSelectFrequency = async (item) => {
    setFrequency(item);
    updateStore("frequency", item);
  };

  const handleSelectCurrencyType = async (currency) => {
    if(!firstLoad) dispatch(planSelected({}));
    dispatch(setCurrencyPlan(currency));
    setCurrency(currency);
    setLoadingSumAssured(true);
  };

  const updateStore = async (key, value) => {
    dispatch(
      planSelected({
        ...detailPlanSelected,
        [key]: value,
      })
    );
  };

  const handleClickRedirect = () => {
    return (window.location.href = urlHome);
  };

  useEffect(() => {
    const virtualName = `sol:comprar:${productName}:cotizacion`;
    helpers.getTagVirtual(virtualName);
  }, [productName]);

  useEffect(() => {
    dispatch(setSuscriptionAnswers({}))
  }, [dispatch])

  return (
    <>
      <RideGrid className="section-plans">
        <div className="section-plans_title">
          <h1>
            <span>Personaliza</span> tu plan
          </h1>
        </div>
        <div className="section-plans_content">
          <CreditBankSelector
            list={arrayFinancialEntities}
            defaultValue={financialEntity}
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            clearErrors={clearErrors}
            isSmallScreen={isSmallScreen}
          />
          <CreditDebtAmount
            list={arraySumAssured}
            currencyOptions={currencyOptions}
            defaultValue={sumAssured}
            register={register}
            setValue={setValue}
            watch={watch}
            firstLoad={firstLoad}
            onSelected={handleSelectSumAssured}
            handleSelectCurrencyType={handleSelectCurrencyType}
            loading={loadingSumAssured}
          />
          <CreditTermSetting
            list={arraySavingTime}
            defaultValue={savingTime}
            loading={loadingSumAssured ||loadingSavingTime}
            onSelected={handleSelectSavingTime}
            isGreatterValue={isGreatterValue}
          />
          <RepaymentPercentage
            list={arrayReturnPercentage}
            defaultValue={returnPercentage}
            initialDefault={initialReturnPercentage}
            loading={loadingSumAssured || loadingSavingTime || loadingReturnPercentage}
            onSelected={handleSelectReturnPercentage}
            isGreatterValue={isGreatterValue}
          />
          <PaymentFrequency
            loading={isLoadingData}
            list={arrayPaymentFrequency}
            frequency={frequency}
            onSelected={handleSelectFrequency}
          />
          <DownloadQuote />
        </div>
        <div className="hidden lg:block lg:col-span-5 lg:col-start-8">
          {!isCreditMaxAmount &&
            <CardDetailPlan
              isLoadingData={isLoadingData}
              callAnalyticsAction={analyticsDetailAction}
            />
          }
        </div>
      </RideGrid>
      {showModalError && (
        <ModalError
          open={showModalError}
          icon="RideFiErrorLight"
          title="Ocurrió un error"
          message="Lo sentimos, hubo un problema técnico. Un asesor te llamará para ayudarte con tu cotización."
          buttonText="Ir al inicio"
          onClose={handleClickRedirect}
        />
      )}
    </>
  );
});

export default SectionPlans;
